<template>
	<div class="container-fluid">

		<filter-bar
			:filters="filterBarFilters"
			v-on:filterChange="setDateRange">

			<template v-slot:before>
				<field
					label="Query By:"
					class="mr-4"
					col-class="col-auto"
					:params="{
						type: 'btn-group',
						buttons: [
							{ text: 'Filtered', value: 'filtered' },
							{ text: 'Services', value: 'service-nums' },
						],
						disableNull: true
					}"
					v-on:change="queryByChange"
					v-model="queryBy">
				</field>
			</template>

			<template v-slot:inline>

				<template v-if="queryBy == 'filtered'">

					<field
						class="mr-4"
						label="Employee:"
						col-class="col-auto"
						:params="{ type:'select', options: employeeOptions}"
						v-model="selectedEmployee"
						v-on:change="">
					</field>

					<field
						class="mr-4"
						label="Customer:"
						col-class="col-auto"
						:params="{ type:'select', options: customerOptions}"
						v-model="selectedCustomer"
						v-on:change="">
					</field>
				</template>

				<template v-else-if="queryBy == 'service-nums'">

					<field
						class="mr-4"
						label="Services"
						col-class="col-auto"
						:params="{
							type: 'text',
							placeholder: 'ex. 23443 2366 25222'
						}"
						v-model="selectedServicesText">
					</field>

				</template>

			</template>

		</filter-bar>


		<div v-if="view=='details'" class="container-fluid mt-3 mb-4">

			<div class="d-flex flex-wrap justify-content-center">
				<field
					label="Compare By:"
					col-class="col-auto"
					:params="{
						type: 'btn-group',
						buttons: [
							{ text: 'Service', value: 'service' },
							{ text: 'Category', value: 'category' },
							{ text: 'Customer', value: 'customer' },
							{ text: 'Month/Year', value: 'date' },
						],
						disableNull: true
					}"
					v-on:change="compareByChange"
					v-model="compareBy">
				</field>


				<field
					v-if="['category', 'customer', 'date'].includes(compareBy)"
					class="ml-5"
					:params="{
						type:'btn-group',
						buttons:[
							{text:'List',value:'list'},
							{text:'Chart Profit',value:'chart-profit'},
							{text:'Chart Profit Margin',value:'chart-profit-margin'}
						],
						disableNull: true
					}"
					v-on:change="updateDetailChart"
					v-model="displayType">
				</field>

				<button v-if="view == 'details' && displayType == 'list' && (selectedEmployee || selectedCustomer || selectedServicesText)"
						class="btn btn-light"
						style="margin-left: 10%; margin-bottom: 5px;"
						v-on:click="exportDetail">

					<i class="fa fa-download"></i>
					Export to CSV
				</button>
			</div>

		</div>


		<div class="container-fluid justify-content-center" style="width: 90%">


			<template v-if="selectedEmployee || selectedCustomer || selectedServicesText">

				<service-report-summary
					v-if="view == 'summary'"
					class="w-100 p-4"
					:total-material-cost="totalCosts.material"
					:total-labour-cost="totalCosts.labour"
					:total-equipment-cost="totalCosts.equipment"
					:total-permits-cost="totalCosts.permit"
					:total-revenue="totalCosts.revenue"
					:total-profit="totalCosts.profit"
					:total-profit-margin="totalCosts.profitMargin"
					:show-chart="true"
					:chart-data="chartData"
					:service-ids="serviceNumList">
				</service-report-summary>

				<div v-else-if="view == 'details'" class="display-group">

					<table-list
						v-if="displayType == 'list'"
						class="pt-4"
						:hide-record-count="true"
						:records="compareByData"
						:fields="tableFields"
						:sortable="true">
					</table-list>

					<chart
						v-else
						:key="detailChartKey"
						:datasets="[{
								label: detailChartLegendLabel,
								data: detailChartData,
								backgroundColor: 'rgba(75, 192, 192, 0.2)',
								borderColor: 'rgb(75, 192, 192)',
								borderWidth: 1
							}]"
						:labels="detailChartLabels"
						type="horizontalBar"
						:height="chartHeight">
					</chart>

				</div>

			</template>


			<div v-else class="p-4">
				<widget class="empty-widget alert-info">
					<h3 class="centered-text"> {{ enterInfoMessage }} </h3>
				</widget>
			</div>

		</div>

	</div>

</template>

<script>

import FilterBar from "@/components/FilterBar"
import Field from "@/components/Field"
import TableList from "@/components/TableList"
import Utilities from "@/mixins/Utilities"
import ServiceReportSummary from "@/components/ServiceReportSummary"
import Widget from "@/components/Widget"
import Chart from "@/components/Chart"

export default {
	name: "ReportsServicesAllView",
	components: {
		FilterBar,
		Field,
		TableList,
		ServiceReportSummary,
		Widget,
		Chart
	},
	mixins: [Utilities],
	props: [
		"allDataFetched",
		"allServices",
		"allInvoices",
		"allBatchInvoices",
		"allEmployees",
		"allCustomers",
		"view",				// summary or details
	],
	data() {
		return {
			// Selected in Filter Bar. Used for organizing data
			queryBy: "filtered",		// Query By
			compareBy: "service",		// Compare By
			displayType: "list",		// View

			// Selected in Filter Bar. Used for filtering data
			selectedEmployee: "",
			selectedCustomer: "",
			selectedServicesText: "",
			dateFilters: [],

			// Update this each time chart data changes
			detailChartKey: 0,

			// These fields are always included in the table
			baseTableFields: [
				{label: "Revenue",    			key: "totalRevenue",  	align: "right",  type: "currency"},
				{label: "Materials",  			key: "totalMaterial", 	align: "right",  type: "currency"},
				{label: "Labour", 	 			key: "totalLabour",		align: "right",  type: "currency"},
				{label: "Equipment", 			key: "totalEquipment",	align: "right",  type: "currency"},
				{label: "Permits", 	  			key: "totalPermit", 	align: "right",  type: "currency"},
				{label: "Taxes Collected", 		key: "totalTaxes", 	 	align: "right",  type: "currency"},
				{label: "Gross Profit", 		key: "totalProfits", 	align: "right",  type: "currency"},
				{label: "Gross Profit Margin", 	key: "profitMargin", 	align: "right",  type: "htmlFloat"}
			],
		}
	},

	computed: {

		///////////////////// Used in Summary Table /////////////////////

		totalCosts() {
			let costs = {
				material: 0,
				labour: 0,
				equipment: 0,
				permit: 0,
				revenue: 0,

				profit: null,
				profitMargin: null
			}

			this.filteredServices.forEach(service => {
				costs.material +=	 	service.total_material_costs ? 	parseFloat(service.total_material_costs) 	: 0
				costs.labour += 		service.total_labour_costs ?		parseFloat(service.total_labour_costs) 	: 0
				costs.equipment += 	service.total_equipment_costs ?	parseFloat(service.total_equipment_costs) : 0
				costs.permit += 		service.total_permits ? 			parseFloat(service.total_permits) 			: 0
				costs.revenue += 		service.revenue ? 					parseFloat(service.revenue)					: 0
			})

			costs.total = costs.material + costs.labour + costs.equipment + costs.permit
			costs.profit = costs.revenue - costs.total

			if (!costs.revenue && !costs.profit)
				costs.profitMargin = 0
			else if (!costs.revenue)
				costs.profitMargin = -100
			else if (!costs.profit)
				costs.profitMargin = 100
			else
				costs.profitMargin = costs.profit / costs.revenue * 100

			return costs
		},


		///////////////////// Used in Filter Bar /////////////////////


		filterBarFilters() {
			if (this.queryBy == 'filtered')
				return [{ type: 'date', label: 'Completed:', prop: 'completed', initialDateRange: 'month'}]
			return []
		},

		filteredServices() {

			// Querying by services? use service nums instead
			if (this.queryBy == 'service-nums') {
				return this.allServices.filter(service => {
					return this.serviceNumList.includes(service.job_num)
				})
			}

			// If not, assume we're querying by filtered

			let startDate = this.dateFilters.find(filter => filter[0] == "completed >")
			let endDate = this.dateFilters.find(filter => filter[0] == "completed <=")

			let filtered = this.allServices.filter(service => {

				if (this.queryBy == 'filtered') {

					if (this.selectedEmployee && this.selectedEmployee != service.assigned_to)
						return false
					if (this.selectedCustomer && this.selectedCustomer != service.customer_id)
						return false

					if (startDate && !(service.completed > startDate[1]))
						return false

					if (endDate && !(service.completed <= endDate[1]))
						return false

					// Only add if atleast one filter is set
					return (this.selectedEmployee || this.selectedCustomer)
				}

				else if (this.queryBy == 'service-nums') {
					return this.selectedServices.includes(service.job_num)
				}

				// Do not return by default
				return false
			})

			// Update chart
			this.updateDetailChart()
			return filtered
		},

		serviceNumList() {
			if (this.compareBy == 'service') {
				return this.selectedServices
			} else if (this.compareBy == 'filtered') {
				return this.allServices.map(service => service.job_num)
			}
			return []
		},

		selectedServices() {
			return this.selectedServicesText.split(" ")
				.filter(num => num != "")
		},

		employeeOptions() {
			return this.optionify(this.allEmployees, 'name', false)
		},

		customerOptions() {
			return this.optionify(this.allCustomers, 'name', false)
		},


		///////////////////// Used in Displaying Data /////////////////////


		enterInfoMessage() {
			if (this.queryBy == 'filtered')
				return "Select an employee or customer to begin"
			else if (this.queryBy == 'service-nums')
				return "Enter one or more service numbers to begin"
			else
				return "Invalid data"
		},

		tableFields() {
			let leadingFields = []

			// Add the leading table field(s)
			if (this.compareBy == 'service') {
				leadingFields = [
					{ label: "Service #", key:'job_num' },
					{ label: "Category", key:'categoryName' },
					{ label: "Customer", key:'customerName' },
					{ label: "Completed Date", key:'completedDate', type: "date" }
				]
			} else if (this.compareBy == 'category') {
				leadingFields = [{label: "Category",key:'name'}, {label: "# of Services", key: "serviceCount"}]
			} else if (this.compareBy == 'customer') {
				leadingFields = [{label: "Customer",key:'name'}, {label: "# of Services", key: "serviceCount"}]
			} else if (this.compareBy == 'date') {
				leadingFields = [{label: "Month/Year",key:'name', type:'date'}, {label: "# of Services", key: "serviceCount"}]
			}

			return leadingFields.concat(this.baseTableFields)
		},

		mutatedServices() {

			return this.filteredServices.map(service => {
				service.completedDate = this.formatDateTimeToLocal(service.completed)

				service.totalMaterial = service.total_material_costs ? 		parseFloat(service.total_material_costs) 	: 0
				service.totalLabour = service.total_labour_costs ? 			parseFloat(service.total_labour_costs)  	: 0
				service.totalEquipment = service.total_equipment_costs ?	parseFloat(service.total_equipment_costs)	: 0
				service.totalPermit = service.permit_cost ? 				parseFloat(service.permit_cost) 			: 0
				service.totalRevenue = service.revenue ? 					parseFloat(service.revenue)					: 0

				service.totalCosts = service.totalMaterial + service.totalLabour + service.totalEquipment + service.totalPermit
				service.rawProfits = service.totalRevenue - service.totalCosts
				this.setProfitMargin(service)

				service.totalMaterial = service.totalMaterial.toCurrency()
				service.totalLabour = service.totalLabour.toCurrency()
				service.totalEquipment = service.totalEquipment.toCurrency()
				service.totalPermit = service.totalPermit.toCurrency()
				service.totalRevenue = service.totalRevenue.toCurrency()
				service.totalCosts = service.totalCosts.toCurrency()
				service.totalProfits = service.rawProfits.toCurrency()
				service.totalTaxes = service.total_taxes ? service.total_taxes.toCurrency() : "0.00"

				service.customerName = service.Customer.name
				return service
			})
		},

		chartData() {
			return [
				this.totalCosts.material.toCurrency(),
				this.totalCosts.labour.toCurrency(),
				this.totalCosts.equipment.toCurrency(),
				this.totalCosts.permit.toCurrency(),
				//0,
				this.totalCosts.profit.toCurrency()
			]
		},

		compareByData() {
			// Restructure data for table/charts based on compare by selected

			// Comparing by service? Just return filtered services
			if (this.compareBy == 'service') {
				return this.mutatedServices
			} else if (this.compareBy == 'customer') {
				return this.mapServicesToGroups('customer')
			} else if (this.compareBy == 'category') {
				return this.mapServicesToGroups('category')
			} else if (this.compareBy == 'date') {
				return this.mapServicesToGroups('date')
			}
		},

		//////////////////// Used when displaying CompareBy Charts ////////////////////////

		detailChartLabels() {
			return this.compareByData.map(item => item.name)
		},

		chartHeight() {
			if (this.detailChartLabels.length <= 12)
				return null

			return this.detailChartLabels.length * 30
		},

		detailChartLegendLabel() {
			if (this.displayType == 'chart-profit')
				return "Net Profit %"
			else if (this.detailType == 'chart-profit-margin')
				return "New Revenue $"
			else
				return ""
		},

		detailChartData() {

			return this.compareByData.map(item => {

				if (this.displayType == 'chart-profit') {
					return item.rawProfits.toFixed(2)
				} else if (this.displayType == 'chart-profit-margin')
					return item.rawProfitMargin.toFixed(1)

				return []
			})

		},

	},


	methods: {

		updateDetailChart() {
			this.$nextTick(() => {
				this.detailChartKey += 1
			})
		},

		queryByChange(queryBy) {
			// Clear previous filters upon switching tabs

			if (queryBy == 'service-nums') {
				this.selectedEmployee = ""
				this.selectedCustomer = ""
			} else if (queryBy == 'filtered') {
				this.selectedServicesText = ""
			}
		},

		compareByChange(compareBy) {

			// If we're changing into "Service", move to displayType == list
			// No graphs should be available for service comparison.  Otherwise, update charts
			if (compareBy == 'service')
				this.displayType = "list"
			else
				this.updateDetailChart()
		},

		setDateRange(filters) {
			this.dateFilters = filters

			if (!this.allDataFetched) {
				let dateFilter = this.dateFilters.find(filter => filter[0] == "completed >")
				let oneMonthAgo = this.moment().subtract(1, 'month').format("YYYY-MM-DD")
				if (dateFilter && (!dateFilter[1] || dateFilter[1] < oneMonthAgo)) {	// Date further back than a month ago? Update first
					this.$emit("fetchAllData")
					return									// Exit (we come back after data is retrieved)
				}
			}

			//this.filterServices()
		},


		mapServicesToGroups(groupType) {

			// Push to here as we find new groups
			let groupList = []

			// Go through each service.  For the group its tied to, add it
			this.filteredServices.forEach(service => {

				let group

				//////////////////////////////////////////////////////////////////
				// Change here when adding CompareBys

				if (groupType == 'customer')
					group = groupList.find(group => service.customer_id == group.id)
				else if (groupType == 'category')
					group = groupList.find(group => service.category_id == group.id)
				else if (groupType == 'date')
					group = groupList.find(group => this.moment(service.completed).format("YYYY-MM") == group.id)
				//////////////////////////////////////////////////////////////////


				// Exising group isnt found? Make it
				if (!group) {
					group = {
						services: [],
						serviceCount: 0,

						// Zero out all the cost values
						totalMaterial: 0,
						totalEquipment: 0,
						totalLabour: 0,
						totalPermit: 0,
						totalTaxes: 0,
						totalRevenue: 0
					}
					groupList.push(group)
				}

				//////////////////////////////////////////////////////////////////
				// Change here when adding CompareBys

				// Set id
				if (groupType == 'customer') {
					group.id = service.customer_id
					group.name = service.Customer.name
				} else if (groupType == 'category') {
					group.id = service.category_id
					group.name = service.categoryName
				} else if (groupType == 'date') {
					group.id = this.moment(service.completed).format("YYYY-MM")
					group.name = this.moment(service.completed).format("MMM YYYY")
				}
				//////////////////////////////////////////////////////////////////


				// Add service ID to their list (NOT JOB ID), increment count
				group.services.push(service.job_num)
				group.serviceCount += 1

				// Update costs
				if ("total_material_costs" in service && service.total_material_costs != null)
					group.totalMaterial += parseFloat(service.total_material_costs)
				if ("total_equipment_costs" in service && service.total_equipment_costs != null)
					group.totalEquipment += parseFloat(service.total_equipment_costs)
				if ("total_labour_costs" in service && service.total_labour_costs != null)
					group.totalLabour += parseFloat(service.total_labour_costs)
				if ("permit_cost" in service && service.permit_cost != null)
					group.totalPermit += parseFloat(service.permit_cost)

				group.totalRevenue += parseFloat(service.revenue)
				group.totalTaxes += parseFloat(service.total_taxes)
			})

			// Go through each user and solidify totals (ex. float -> string)
			return groupList.map(group => {

				// Calculate totals
				group.totalCosts = group.totalMaterial + group.totalEquipment + group.totalLabour + group.totalPermit
				group.rawProfits = group.totalRevenue - group.totalCosts
				this.setProfitMargin(group)

				// Format money
				group.totalMaterial = group.totalMaterial.toCurrency()
				group.totalEquipment = group.totalEquipment.toCurrency()
				group.totalLabour = group.totalLabour.toCurrency()
				group.totalPermit = group.totalPermit.toCurrency()
				group.totalCosts = group.totalCosts.toCurrency()
				group.totalRevenue = group.totalRevenue.toCurrency()
				group.totalTaxes = group.totalTaxes.toCurrency()
				group.totalProfits = group.rawProfits.toCurrency()

				return group
			})
		},

		setProfitMargin(group) {
			// A group contains total cost values (material, labour, etc)
			// and is updated directly
			let profitMargin

			if (!group.totalRevenue && !group.rawProfits)
				profitMargin = 0
			else if (!group.totalRevenue)
				profitMargin = -100
			else if (!group.rawProfits)
				profitMargin = 100
			else
				profitMargin = group.rawProfits / group.totalRevenue * 100

			group.rawProfitMargin = profitMargin

			let profitMarginColor = ""
			let profitMarginIcon = ""

			if (profitMargin < 0) {
				profitMarginColor = "text-danger"
				profitMarginIcon = "fa fa-caret-down"
			} else if (profitMargin > 0) {
				profitMarginColor = "text-success"
				profitMarginIcon = "fa fa-caret-up"
			}

			group.profitMargin = "<span class='" + profitMarginColor + "'> " +
				"<i class='" + profitMarginIcon + "'></i>" +
				" " + profitMargin.toFixed(2) + " %" +
				"</span>"

		},

		/*
		filterServices() {

			// Filter all services after new filters are selected
			this.$nextTick(() => {

				let startDate = this.dateFilters.find(filter => filter[0] == "completed >")
				let endDate = this.dateFilters.find(filter => filter[0] == "completed <=")

				this.filteredServices = this.allServices.filter(service => {

					if (this.queryBy == 'filtered') {

						if (this.selectedEmployee && this.selectedEmployee != service.assigned_to)
							return false
						if (this.selectedCustomer && this.selectedCustomer != service.customer_id)
							return false

						if (startDate && !(service.completed > startDate[1]))
							return false

						if (endDate && !(service.completed <= endDate[1]))
							return false

						// Only add if atleast one filter is set
						return (this.selectedEmployee || this.selectedCustomer)
					}

					else if (this.queryBy == 'service-nums') {
						return this.selectedServices.includes(service.job_num)
					}

					// Do not return by default
					return false
				})

				// Update chart
				this.updateDetailChart()

			})
		},


		 */
		exportDetail() {

			let headerRow = this.tableFields.map(field => field.label)

			//let rows = this.computedServices.map(row => {
			let rows = this.compareByData.map(row => {
				let renderedRow = []
				this.tableFields.forEach(field => {
					if (!row[field.key])
						renderedRow.push("")
					else {
						// If profitMargin use a different value
						// (ex. usually <html....>, instead use raw value
						if (field.key == "profitMargin")
							renderedRow.push(row["rawProfitMargin"] + "%")
						else
							renderedRow.push(row[field.key])
					}
				})
				return renderedRow
			})

			rows.unshift(headerRow)

			let filename = "ServicesReport_by_"+this.compareBy+".csv"

			this.exportToCSV(filename,rows)
		},
	}
}
</script>

<style scoped>

.empty-widget {
	min-height: 100px;
	background-color:#d1ecf1;
	border-color:#bee5eb
}

.centered-text {
	position: absolute;
	text-align: center;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.display-group {
	padding-bottom: 20%;
}


</style>
